/**
 * Dash0 RUM (Real User Monitoring) for the customer-facing Portal (my.pubble.cloud).
 *
 * Unlike the staff app (PubbleCloud.Frontend/ClientMud/JsDash0), the Portal has NO login and
 * NO username whitelist: every visitor is an anonymous advertiser opening a proof / delivery
 * link. We therefore initialize FULL RUM immediately on load for everyone — errors, page views,
 * network and web-vitals — because the Portal was previously blind on the client side: a failed
 * JS-interop editor mount or a render crash left no telemetry at all, only a confused customer.
 *
 * The bundle exposes a `window.Dash0` global (UMD library name "Dash0") whose surface is a
 * deliberate subset of the staff bundle, keeping the same interop method names so the shared
 * patterns (Dash0.reportDiagnostic / Dash0.setRoute / Dash0.logError) work identically here.
 *
 * Build commands (run from this JsDash0 folder):
 *   npm run build        - Development build  (../wwwroot/js/pubble.Dash0.js)
 *   npm run build:prod   - Production build   (../wwwroot/js/pubble.Dash0.min.js)
 *   npm run watch        - Watch mode
 */
import { init, addSignalAttribute, reportError, sendEvent } from "@dash0/sdk-web";
import type { UrlAttributeRecord } from "@dash0/sdk-web";

// Portal links carry their authorization in the query: `sig` opens a customer's order, proof or invoice.
// Whoever can read telemetry must not be able to replay it, so these values never leave the browser.
const CREDENTIAL_PARAMS = /([?&](?:sig|token|access_token|code|password)=)[^&#]*/gi;

function redact(url: string): string {
    return url.replace(CREDENTIAL_PARAMS, "$1REDACTED");
}

function scrubUrlAttributes(attr: UrlAttributeRecord): UrlAttributeRecord {
    const scrubbed: UrlAttributeRecord = { ...attr, "url.full": redact(attr["url.full"]) };
    if (attr["url.query"]) scrubbed["url.query"] = redact("?" + attr["url.query"]).slice(1);
    return scrubbed;
}

const SERVICE_NAME = "pubble-cloud-portal";

let isInitialized = false;
let currentRoute = "";

// Build the traceparent propagator match-list: same-origin /api/* only. The Portal's BFF serves
// both the SPA and its /api/* endpoints from the same origin, so propagating W3C traceparent lets
// a browser request join its server-side trace end-to-end (e.g. the proof load that 500'd on a
// stale enum is then visible from the click all the way to the server exception). Scoped to
// same-origin /api/* so we never send the header cross-origin (no CORS surprises). Returns [] if
// there's no window, so init never propagates to anything we didn't explicitly allow.
function buildTraceparentPropagators() {
    try {
        if (typeof window === "undefined") return [];
        const origin = window.location.origin.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        return [{ type: "traceparent" as const, match: [new RegExp(`^${origin}/api/`)] }];
    } catch {
        return [];
    }
}

function readServiceVersion(): string | undefined {
    try {
        // Stamped on <body data-app-version="..."> by the host page (App.razor) if available.
        const v = typeof document !== "undefined"
            ? document.body?.getAttribute("data-app-version")
            : null;
        return v && v.trim() && !v.includes("BuildId") ? v.trim() : undefined;
    } catch {
        return undefined;
    }
}

function initialize() {
    if (isInitialized) return;
    try {
        init({
            serviceName: SERVICE_NAME,
            serviceVersion: readServiceVersion(),
            endpoint: {
                url: "https://ingress.eu-west-1.aws.dash0.com",
                authToken: "auth_GMVFrLShT4XcraBvSk8KDHvSjQT6iKiU",
            },
            propagators: buildTraceparentPropagators(),
            urlAttributeScrubber: scrubUrlAttributes,
            // Full instrumentation: anonymous customers, and we want the complete picture
            // (errors + page views + network + web-vitals). No errorsOnly gating here.
            ignoreUrls: [
                /firestore\.googleapis\.com/,
                /firebaseio\.com/,
                /firebaseinstallations\.googleapis\.com/,
                /firebaseremoteconfig\.googleapis\.com/,
                /identitytoolkit\.googleapis\.com/,
                /securetoken\.googleapis\.com/,
            ],
        });
        isInitialized = true;
    } catch (e) {
        // Telemetry must never break the Portal — swallow init failures.
        // eslint-disable-next-line no-console
        console.warn("[Dash0] Portal RUM init failed", e);
    }
}

// The current SPA route. Blazor navigation has no page reloads, so the URL the SDK auto-captures
// can be the initial one; the layout pushes the live route here via Dash0.setRoute so every
// signal/error knows WHERE it happened (e.g. ".../proof/1568485/request-changes").
export function setRoute(route: string) {
    try {
        currentRoute = redact(route || "");
        if (isInitialized) {
            addSignalAttribute("pubble.page.route", currentRoute || "unknown");
        }
    } catch (e) {
        console.warn("[Dash0] setRoute failed", e);
    }
}

// Forward a .NET client exception (caught by the Blazor ErrorBoundary) to Dash0. Best-effort.
export function logError(error: string | Error, options?: { componentStack?: string }) {
    try {
        if (!isInitialized) initialize();
        reportError(error, options);
    } catch (e) {
        console.warn("[Dash0] logError failed", e);
    }
}

// Always-sent diagnostic event. Mirrors the staff bundle's Dash0.reportDiagnostic so the Portal's
// Dash0ErrorBoundaryLogger uses the identical interop call. Customer-facing render crashes
// (e.g. an editor that won't mount) surface here with their type, message and route.
export function reportDiagnostic(
    name: string,
    severity: "INFO" | "WARN" | "ERROR",
    attributes?: Record<string, string>,
) {
    try {
        if (!isInitialized) initialize();
        sendEvent(name, {
            attributes: {
                "pubble.page.route": currentRoute || "unknown",
                ...(attributes ?? {}),
            },
            severity,
        });
    } catch (e) {
        console.warn("[Dash0] reportDiagnostic failed", e);
    }
}

// Initialize immediately on bundle load — there is no per-user gating to wait for.
initialize();
